import { http } from '@noor/api';
import { env } from '@noor/config';
import * as z from 'zod';
import {
  lifecycleState,
  storefrontItemSchema,
  type StorefrontEntity,
  type StorefrontItem,
  type StorefrontSearch,
} from '../model/schema';

export const STOREFRONT_CONFIG = {
  products: { path: 'products', key: 'product', list: 'products', entityType: 'product' },
  categories: {
    path: 'product-categories',
    key: 'category',
    list: 'categories',
    entityType: 'product_category',
  },
  restaurants: {
    path: 'restaurants',
    key: 'restaurant',
    list: 'restaurants',
    entityType: 'restaurant',
  },
  promotions: {
    path: 'promotions',
    key: 'promotion',
    list: 'promotions',
    entityType: 'promotion',
  },
} as const;
const base = { baseURL: env.VITE_EATS_API_URL };
const record = z.record(z.string(), z.unknown());
const daySchedule = z.object({
  day_of_week: z.string().optional(),
  open_time: z.string().optional(),
  close_time: z.string().optional(),
  order_from: z.string().optional(),
  order_to: z.string().optional(),
  is_closed: z.boolean().optional(),
});
const metadata = z
  .object({
    total: z.coerce.number().optional(),
    page_count: z.coerce.number().optional(),
  })
  .passthrough();

export function normalizeStorefrontList(raw: unknown, entity: StorefrontEntity) {
  const root = record.parse(raw);
  const data = record.parse(root.data ?? {});
  const rows = z
    .array(storefrontItemSchema)
    .parse(
      root.list ??
        data[STOREFRONT_CONFIG[entity].list] ??
        root[STOREFRONT_CONFIG[entity].list] ??
        [],
    );
  const meta = metadata.parse(root.meta ?? data.pagination ?? root.pagination ?? {});
  return { rows, total: meta.total ?? rows.length, pageCount: meta.page_count };
}

function filters(
  entity: StorefrontEntity,
  params: StorefrontSearch,
): Record<string, string> {
  return {
    ...(params.merchant_id ? { merchant_id: params.merchant_id } : {}),
    ...(entity !== 'restaurants' && params.restaurant_id
      ? { restaurant_id: params.restaurant_id }
      : {}),
  };
}

export const storefrontApi = {
  async reorderCategories(positions: { category_id: string; sort_order: number }[]) {
    if (
      positions.length === 0 ||
      positions.length > 50 ||
      positions.some(({ sort_order }) => !Number.isInteger(sort_order) || sort_order < 1)
    ) {
      throw new RangeError(
        'Expected 1–50 category positions with sort_order starting at 1',
      );
    }
    const raw = await http.put<unknown>(
      '/api/v1/admin/product-categories/order',
      { positions },
      base,
    );
    const data = record.parse(record.parse(raw).data ?? raw);
    return z
      .object({
        reordered_category_ids: z.array(z.string()),
        skipped_category_ids: z.array(z.string()),
      })
      .parse(data);
  },
  async list(entity: StorefrontEntity, params: StorefrontSearch, signal?: AbortSignal) {
    const config = STOREFRONT_CONFIG[entity];
    if (params.status === 'published') {
      const raw = await http.get<unknown>(`/api/v1/admin/${config.path}/published`, {
        ...base,
        ...(signal ? { signal } : {}),
        query: {
          ...filters(entity, params),
          'pagination.limit': String(params.pageSize),
          'pagination.offset': String((params.page - 1) * params.pageSize),
        },
      });
      return normalizeStorefrontList(raw, entity);
    }
    // The spec has no unpublished endpoint or lifecycle filter for three of
    // these catalogues. Collect the filtered admin catalogue before paginating
    // non-live rows, so counts/pages never describe a partially filtered page.
    const catalogue: StorefrontItem[] = [];
    const limit = 100;
    for (let offset = 0; ; ) {
      const raw = await http.get<unknown>(`/api/v1/admin/${config.path}`, {
        ...base,
        ...(signal ? { signal } : {}),
        query: {
          ...filters(entity, params),
          ...(entity === 'restaurants' ? { include_hidden: 'true' } : {}),
          'pagination.limit': String(limit),
          'pagination.offset': String(offset),
        },
      });
      const result = normalizeStorefrontList(raw, entity);
      catalogue.push(...result.rows);
      if (result.rows.length === 0 || offset + result.rows.length >= result.total) break;
      offset += result.rows.length;
    }
    const rows = catalogue.filter((item) =>
      ['created', 'ready_for_life'].includes(
        lifecycleState(item.lifecycle_status ?? undefined),
      ),
    );
    const offset = (params.page - 1) * params.pageSize;
    return {
      rows: rows.slice(offset, offset + params.pageSize),
      total: rows.length,
      pageCount: Math.ceil(rows.length / params.pageSize),
    };
  },

  async detail(
    entity: StorefrontEntity,
    id: string,
    params: StorefrontSearch,
    signal?: AbortSignal,
  ) {
    const config = STOREFRONT_CONFIG[entity];
    const published = params.status === 'published';
    const path = published
      ? `/api/v1/admin/${config.path}/${encodeURIComponent(id)}/published`
      : `/api/v1/admin/${config.path}/${encodeURIComponent(id)}`;
    const root = record.parse(
      await http.get<unknown>(path, {
        ...base,
        ...(signal ? { signal } : {}),
        query:
          published && ['products', 'promotions'].includes(entity) && params.restaurant_id
            ? { restaurant_id: params.restaurant_id }
            : {},
      }),
    );
    const data = record.parse(root.data ?? root);
    return {
      item: storefrontItemSchema.parse(data[config.key]),
      clientVisible: z.boolean().optional().parse(data.client_visible),
      weekSchedule: z.array(daySchedule).parse(root.list ?? data.week_schedule ?? []),
    };
  },

  async changePublication(
    entity: StorefrontEntity,
    id: string,
    action: 'publish' | 'unpublish',
    reason?: string,
  ) {
    return http.post(
      `/api/v1/admin/entities/${STOREFRONT_CONFIG[entity].entityType}/${encodeURIComponent(id)}/${action}`,
      action === 'unpublish' && reason ? { reason } : {},
      base,
    );
  },
};
